import {
    AfterViewInit,
    Component,
    computed,
    Directive,
    effect,
    ElementRef,
    HostListener,
    Inject,
    input,
    model,
    OnDestroy,
    output,
    Renderer2,
    signal,
    viewChild,
    viewChildren,
} from '@angular/core';
import { AclDirective } from 'app/shared/acl/acl-resource.directive';
import { AutofocusDirective } from 'app/shared/form-utils/autofocus.directive';
import { CheckboxCheckedDirective } from 'app/shared/form-utils/checkbox-checked.directive';
import { PlaceholderDirective } from 'app/shared/form-utils/placeholder.directive';
import { HighlightService } from 'app/shared/pipes/highlight.service';
import { modulo } from 'app/shared/utils';
import { OverlayRef, POSITION_TARGET, PositionTarget } from '../overlays';
import { Option } from './option.model';


@Directive({
    selector: '.dropdown-item'
})
export class DropdownItem {}


@Component({
    selector: 'ui-select-box-dropdown-menu',
    imports: [
        AclDirective,
        DropdownItem,
        AutofocusDirective,
        CheckboxCheckedDirective,
        PlaceholderDirective,
    ],
    host: {
        'animate.enter': 'fade-enter',
        'animate.leave': 'fade-leave',
        'style': '--fade-duration: 0.2s',
        'class': 'dropdown-menu shadow',
    },
    styles: [
        `
        :host {
            display: block;
            background: white;
            z-index: 1060;
        }
        .dropdown-item {
            display: block;
            white-space: break-spaces;
            padding: .25rem .5rem;
            color: #212529;
        }
        .dropdown-item.active {
            color: #1583D6;
            background-color: #F1F4F8;
        }
        .dropdown-item span {
            vertical-align: middle;
            padding-left: 1em;
        }
        .options-wrapper {
            overflow-x: hidden;
            overflow-y: auto;
            max-height: 15em;
        }
        button {
            margin: 5px;
        }
        input[type='text'] {
            width: 100%;
        }
        input[type='checkbox'] {
            vertical-align: middle;
        }
        .input-group-text {
            background: none;
            border: none;
        }
        .search-input {
            border: 0;
        }
        .search-bar {
            border: 1px #eaeaea solid;
        }
        `,
    ],
    template: `
        <div class="p-1">
            <div class="input-group search-bar">
                <input
                    #editableArea
                    class="form-control search-input"
                    [value]="filterText()"
                    (input)="filterText.set($event.target!.value)"
                    placeholder="Search here..."
                    autofocus>
                @if (clearVisible()) {
                    <div class="input-group-append">
                        <button class="btn btn-link clear-search input-group-text" (click)="filterText.set('')"><i class="fa fa-close"></i></button>
                    </div>
                }
            </div>
        </div>

        <div class="options-wrapper" #optionsWrapper>
            @for (option of filteredOptions(); track option.key) {
                <div>
                    @if (option.key == 'selectAll') {
                        <label class="dropdown-item select-all-item"
                            [class.active]="activeItemIndex() == $index"
                            (mouseover)="hoverOption($index)">
                            <input type="checkbox" [checked]="selectedAll()" (click)="toggleAll()">
                            <span>{{ allCaption }}</span>
                        </label>
                    }
                    @else {
                        <label *apAcl="option.aclResourceName"
                            class="dropdown-item option-item"
                            [class.active]="activeItemIndex() == $index"
                            (mouseover)="hoverOption($index)">
                            <input type="checkbox" [checked]="selectedOptions().includes(option.key)" (click)="selectOption(option.key)">
                            <span [innerHTML]="option.title"></span>
                        </label>
                    }
                </div>
            }
        </div>
        <div>
            <button class="btn btn-secondary" (click)="commitOptions('done')">
                Done
            </button>
        </div>
    `,
})
export class DropdownMenuComponent implements AfterViewInit, OnDestroy {

    private editableArea = viewChild.required<ElementRef>('editableArea');

    private optionsWrapper = viewChild.required<ElementRef>('optionsWrapper');

    private dropdownItems = viewChildren(DropdownItem, { read: ElementRef });

    availableOptions = input<Option[]>([]);

    activeOptions = input<Option[]>([]);

    showAllOption = input(true);

    selectOptionChange = output<string[]>();

    allOptions = computed(() => {
        const showAllOption = this.showAllOption();
        const availableOptions = this.availableOptions();
        if (showAllOption) {
            return [{ key: 'selectAll', title: this.allCaption }, ...availableOptions];
        } else {
            return availableOptions;
        }
    });

    filterText = model('');

    filteredOptions = computed(() => {
        const text = this.filterText();

        const options: Array<Option & { search: Array<[number, number]> }> = [];
        for (const option of this.allOptions()) {
            const search = this.hs.fuzzySearch(text, option.title);
            if (text === '' || search.length > 0) {
                options.push({ ...option, title: this.hs.highlightSearch(option.title, search), search });
            }
        }

        const score = (option: {key: string, title: string, search: Array<[number, number]>}) => {
            if (!option.title.length) {
                return 0;
            }

            let sum = 0;
            for (const group of option.search) {
                const [start, end] = group;
                sum += end - start;
            }
            return sum / option.title.length;
        };

        options.sort((a, b) => {
            if (score(a) > score(b)) {
                return -1;
            } else if (score(a) < score(b)) {
                return 1;
            } else {
                return 0;
            }
        });

        return options;
    });

    selectedOptions = computed(() => {
        return this.activeOptions().map(option => option.key);
    });

    allCaption = 'Select All';

    clearVisible = computed(() => this.filterText().length > 0);

    showSelectAll = computed(() => this.filterText().length === 0 && this.showAllOption());

    private initiated = false;

    private animationFrame: any;

    activeItemIndex = signal(0);

    constructor(
        private overlayRef: OverlayRef,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private hs: HighlightService,
        @Inject(POSITION_TARGET) private parent: PositionTarget
    ) {
        effect(() => {
            const options = this.filteredOptions();
            this.activeItemIndex.update(index => Math.min(index, options.length));
        });
    }

    ngAfterViewInit() {
        this.startCalculatingPosition();
        setTimeout(() => {
            this.initiated = true;
        });
    }

    setFocus() {
        this.editableArea().nativeElement.focus();
    }

    private startCalculatingPosition() {
        this.calcPosition();
        this.animationFrame = requestAnimationFrame(() => this.startCalculatingPosition());
    }

    toggleAll() {
        if (!this.selectedAll()) {
            this.selectOptionChange.emit(this.allOptions().map((value => value.key)));
        } else {
            this.selectOptionChange.emit([]);
        }
    }

    selectedAll() {
        const available_options_length = this.showSelectAll() ? this.availableOptions.length - 1 : this.availableOptions.length;
        if (available_options_length > 0) {
            return this.selectedOptions.length === available_options_length;
        }
        return false;
    }

    @HostListener('keydown.enter')
    @HostListener('keydown.tab')
    selectOption(key?: string) {
        if (this.dropdownItems().length > 0) {
            if (typeof key === 'undefined') {
                key = this.availableOptions()[this.activeItemIndex()].key;
            }
            if (key === 'selectAll') {
                this.toggleAll();
            } else {
                if (this.selectedOptions().includes(key)) {
                    this.selectOptionChange.emit(this.selectedOptions().filter((value) => value !== key));
                } else {
                    this.selectOptionChange.emit([...this.selectedOptions(), key]);
                }
            }
        }
    }

    commitOptions(reason: string) {
        this.selectOptionChange.emit(this.selectedOptions());
        this.overlayRef.dismiss(reason);
    }

    hoverOption(index: number) {
        this.activeItemIndex.set(index);
    }

    @HostListener('keydown.arrowup')
    moveUp() {
        if (this.dropdownItems().length > 0) {
            this.activeItemIndex.update(index => modulo(index - 1, this.dropdownItems().length));
            this.scrollToActiveOption();
        }
    }

    @HostListener('keydown.arrowdown')
    moveDown() {
        if (this.dropdownItems().length > 0) {
            this.activeItemIndex.update(index => modulo(index + 1, this.dropdownItems().length));
            this.scrollToActiveOption();
        }
    }

    private scrollToActiveOption() {
        const activeEl = this.dropdownItems()[this.activeItemIndex()].nativeElement;
        const container = this.optionsWrapper().nativeElement;
        if (activeEl.offsetTop <= container.scrollTop + activeEl.offsetHeight) {
            container.scrollTop = activeEl.offsetTop - activeEl.offsetHeight;
        }
        if (activeEl.offsetTop + activeEl.offsetHeight > container.scrollTop + container.clientHeight) {
            container.scrollTop = activeEl.offsetTop - container.clientHeight + activeEl.offsetHeight;
        }
    }

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent) {
        if (!this.initiated) {
            return;
        }
        const bb = this.elementRef.nativeElement.getBoundingClientRect();
        if (bb.left <= event.clientX && event.clientX <= bb.right && bb.top <= event.clientY && event.clientY <= bb.bottom) {
            // click inside
            return;
        }
        this.commitOptions('click outside');
    }

    private calcPosition() {
        const dropdownMinWidth = 250;
        const parentRect = this.parent.getBoundingClientRect();
        const el = this.elementRef.nativeElement;
        this.renderer.setStyle(el, 'position', 'absolute');
        this.renderer.setStyle(el, 'width', `${Math.max(parentRect.width, dropdownMinWidth)}px`);
        this.renderer.setStyle(el, 'left', `${parentRect.left}px`);
        const { height } = el.getBoundingClientRect();
        const top = parentRect.bottom + height > window.innerHeight ? parentRect.top - height - 5 : parentRect.bottom;
        this.renderer.setStyle(el, 'top', `${top}px`);
    }

    ngOnDestroy() {
        cancelAnimationFrame(this.animationFrame);
    }
}
